import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import { Typography, theme } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import {
  EventCreationType,
  useGetProfileBookingsQuery,
  useGetProfileDeskSchedulesQuery,
  useGetProfileSeriesInstancesQuery,
} from 'generated/graphql';
import { CancelAutoBookings } from './CancelAutoBookings';
import {
  ProfileReservation,
  getReservationKind,
} from '../BookingsList/utils/getReservationActions';
import { groupBookingsBySeries } from '../BookingsList/utils/groupBookingSeries';
import {
  SeriesInstance,
  hasReservationEnded,
  withSeriesSummaryInstances,
} from '../BookingsList/utils/seriesInstances';
import { useProfileDeskReservationActions } from '../BookingsList';
import { DesksList, DesksListSkeleton } from './DesksList';

type DesksPanelProps = {
  userId: string;
  isProfileSidebarOpen?: boolean;
  onNavigateAway?: () => void;
  onModalOpenChange?: (open: boolean) => void;
};

// How many days of flex reservations to load per page. The first page shows
// the next 30 days; scrolling to the bottom loads the next 30 days, and so on,
// until a page returns nothing new.
const WINDOW_INCREMENT_DAYS = 30;

/**
 * The right-hand column of the fullscreen profile: a "Desks" header over the
 * user's desk reservations. Assigned desks are pinned on top (one card per
 * assignment series); flex / hotel reservations follow, ordered by date across
 * a rolling window that grows as the list is scrolled. Reservation actions,
 * modals and map navigation are shared with the sider via
 * `useProfileDeskReservationActions`.
 */
export const DesksPanel = ({
  userId,
  isProfileSidebarOpen,
  onNavigateAway,
  onModalOpenChange,
}: DesksPanelProps): JSX.Element => {
  const { t } = useTranslation('peopleProfileSidebar', { keyPrefix: 'desks' });
  const { token } = theme.useToken();

  const [windowDays, setWindowDays] = useState(WINDOW_INCREMENT_DAYS);
  const [hasMore, setHasMore] = useState(true);

  // Anchored to when the panel opened, like `startIso` below, so the value is
  // stable across renders instead of churning child props every render.
  const [currentTime] = useState(() => moment());

  // Anchor the window to the moment the panel opened, so the query variables
  // stay stable across renders (a fresh moment() each render would churn them).
  //
  // Starts at *now* rather than start-of-day: unassigning a user leaves the
  // current occurrence behind, truncated to the moment of unassignment, and a
  // start-of-day window would still return that dead occurrence and pin it as
  // an assigned desk. `getDeskReservationsByUserIds` bounds on overlap (a
  // reservation spanning the start is returned, verified against staging), so
  // anchoring here drops anything already over while keeping in-progress
  // reservations. Matches the legacy profile page, which windows from
  // `moment().startOf('minute')` for the same reason.
  const [startIso] = useState(() => moment().toISOString());
  const endIso = useMemo(
    () => moment(startIso).add(windowDays, 'days').endOf('day').toISOString(),
    [startIso, windowDays]
  );

  const { data, previousData, loading, error } = useGetProfileBookingsQuery({
    variables: { userId, start: startIso, end: endIso },
    skip: !userId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  // Growing the window changes the query variables, so Apollo clears `data`
  // until the wider result arrives. Fall back to the last result so the list
  // and scroll position stay put instead of flashing empty mid-scroll.
  const effectiveData = data ?? previousData;

  const rawReservations = useMemo(
    () => effectiveData?.getDeskReservationsByUserIds?.[0]?.reservations ?? [],
    [effectiveData]
  );

  // Assigned desks: one card per assignment series (the window can return one
  // instance per matching day), pinned on top and sorted by desk name.
  const assigned = useMemo(() => {
    const seen = new Set<string>();
    const result: ProfileReservation[] = [];
    rawReservations.forEach((reservation) => {
      if (getReservationKind(reservation) !== 'assignment') {
        return;
      }
      const key = reservation.seriesId ?? reservation.id;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      result.push(reservation);
    });
    return result.sort((a, b) =>
      a.seat.name.localeCompare(b.seat.name, undefined, { numeric: true })
    );
  }, [rawReservations]);

  // Flex / hotel reservations: each dated booking that has not already ended,
  // ordered chronologically across the loaded window.
  const flex = useMemo(() => {
    return rawReservations
      .filter((reservation) => getReservationKind(reservation) === 'booking')
      .filter(
        (reservation) =>
          !hasReservationEnded(reservation, reservation.seat.location.timezone)
      )
      .sort((a, b) => {
        if (a.startTime === b.startTime) {
          return a.seat.name.localeCompare(b.seat.name, undefined, {
            numeric: true,
          });
        }
        return a.startTime < b.startTime ? -1 : 1;
      });
  }, [rawReservations]);

  // One entry per booking: a multi-day booking is one instance per day sharing
  // a series id, and shows as a single card summarizing the span rather than a
  // card per day.
  const windowedGroups = useMemo(() => groupBookingsBySeries(flex), [flex]);

  // The window only holds the instances of a series that fall inside it, so a
  // series running past the window would be summarized up to its edge ("Today
  // and 30 more days" for a year-long series), and the count would change as
  // more was scrolled in. Look up every instance of each visible series so the
  // date line describes the whole booking regardless of the window.
  const seriesIds = useMemo(
    () =>
      windowedGroups
        .map((group) => group.reservation.seriesId)
        .filter((seriesId): seriesId is string => !!seriesId),
    [windowedGroups]
  );
  const { data: seriesData, previousData: previousSeriesData } =
    useGetProfileSeriesInstancesQuery({
      variables: { seriesIds },
      skip: seriesIds.length === 0,
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    });

  // Scrolling more of the window in adds series ids, which changes the query
  // variables and clears `data` until the wider result arrives. Fall back to
  // the last result so the cards already on screen keep their full-series
  // label instead of flashing back to the windowed count mid-scroll.
  const effectiveSeriesData = seriesData ?? previousSeriesData;
  const instancesBySeriesId = useMemo(() => {
    const map: Record<string, SeriesInstance[]> = {};
    effectiveSeriesData?.getDeskReservationInstancesBySeriesIds?.forEach(
      (series) => {
        map[series.seriesId] = series.reservations;
      }
    );
    return map;
  }, [effectiveSeriesData]);

  // Until the lookup lands (or if it fails) each card falls back to its
  // windowed instances, so the list renders immediately either way.
  const flexGroups = useMemo(
    () => withSeriesSummaryInstances(windowedGroups, instancesBySeriesId),
    [windowedGroups, instancesBySeriesId]
  );

  // Upcoming reservations created by auto desk booking, driving the "Cancel
  // auto-bookings" action's count and disabled state. Counted per instance
  // (not per booking) — the modal's copy is a reservation count.
  const autoBooked = useMemo(
    () =>
      flex.filter(
        (reservation) =>
          reservation.creationType === EventCreationType.Automatic
      ),
    [flex]
  );

  const assignmentDeskIds = useMemo(
    () =>
      assigned
        .filter((reservation) => !!reservation.seriesId)
        .map((reservation) => reservation.seat.id)
        .filter((deskId, index, deskIds) => deskIds.indexOf(deskId) === index),
    [assigned]
  );

  const { data: schedulesData } = useGetProfileDeskSchedulesQuery({
    variables: { deskIds: assignmentDeskIds },
    skip: assignmentDeskIds.length === 0,
    errorPolicy: 'all',
  });

  const recurrenceByDeskId = useMemo(() => {
    const map: Record<string, string | null> = {};
    schedulesData?.getSharedDesksSchedules?.forEach((deskSchedule) => {
      if (!deskSchedule?.id) {
        return;
      }
      const userSchedule = deskSchedule.schedule?.find(
        (entry) => entry?.id === userId
      );
      map[deskSchedule.id] = userSchedule?.recurrence ?? null;
    });
    return map;
  }, [schedulesData, userId]);

  // Detect the end of the list: once widening the window stops adding flex
  // reservations, there are no more future dates to load.
  const loadStateRef = useRef({ windowDays: WINDOW_INCREMENT_DAYS, count: -1 });
  useEffect(() => {
    if (loading) {
      return;
    }
    const previous = loadStateRef.current;
    if (windowDays > previous.windowDays && flex.length === previous.count) {
      setHasMore(false);
    }
    loadStateRef.current = { windowDays, count: flex.length };
  }, [loading, windowDays, flex.length]);

  const handleLoadMore = useCallback(() => {
    setWindowDays((current) => current + WINDOW_INCREMENT_DAYS);
  }, []);

  const {
    isCurrentPerson,
    isAdmin,
    onCardClick,
    onCheckIn,
    onMenuAction,
    modals,
  } = useProfileDeskReservationActions({
    userId,
    isProfileSidebarOpen,
    onModalOpenChange,
    onNavigateAway,
    recurrenceByDeskId,
  });

  const loadingMore = loading && windowDays > WINDOW_INCREMENT_DAYS;

  return (
    <Panel>
      <Header $borderColor={token.colorBorderSecondary}>
        <Tab $activeColor={token.colorInfo}>
          <TabLabel strong>{t('title')}</TabLabel>
        </Tab>
        <CancelAutoBookings
          userId={userId}
          autoBookedReservations={autoBooked}
          isProfileSidebarOpen={isProfileSidebarOpen}
          onModalOpenChange={onModalOpenChange}
        />
      </Header>
      <ListWrapper>
        {loading && !effectiveData ? (
          <DesksListSkeleton />
        ) : error && !effectiveData ? (
          <ErrorWrapper>
            <Typography.Text type="secondary">
              {t('errorLoading')}
            </Typography.Text>
          </ErrorWrapper>
        ) : (
          <DesksList
            assigned={assigned}
            flex={flexGroups}
            recurrenceByDeskId={recurrenceByDeskId}
            isCurrentPerson={isCurrentPerson}
            isAdmin={isAdmin}
            currentTime={currentTime}
            onCardClick={onCardClick}
            onCheckIn={onCheckIn}
            onMenuAction={onMenuAction}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
          />
        )}
        {modals}
      </ListWrapper>
    </Panel>
  );
};

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

// Separate the first desk card from the "Desks" tab strip (the list itself
// has no top margin). Matches the ~8px gap in the FOCO-149 design.
const ListWrapper = styled.div`
  padding-top: 8px;
`;

// Horizontal margin (not padding) insets the border so the divider lines up
// with the cards' 16px gutter instead of bleeding to the drawer edge. Top
// padding gives the tab strip breathing room below the drawer header divider.
// Flex row so the Cancel auto-bookings action sits at the strip's far right.
const Header = styled.div<{ $borderColor: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 16px;
  padding-top: 8px;
  border-bottom: 1px solid ${({ $borderColor }) => $borderColor};
`;

// inline-flex + fit-content keeps the active-tab underline hugging the label
// exactly (the FOCO-149 DS tab bar underlines only the "Desks" word, not a
// wider strip).
const Tab = styled.div<{ $activeColor: string }>`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  height: 40px;
  border-bottom: 2px solid ${({ $activeColor }) => $activeColor};
`;

// Match the FOCO-149 DS tab label (14px / 22px) so the label — and therefore
// the underline that hugs it — is the right width.
const TabLabel = styled(Typography.Text)`
  font-size: 14px;
  line-height: 22px;
`;

const ErrorWrapper = styled.div`
  padding: 0 16px 16px;
`;
